import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  SimpleGrid,
  Flex,
  Icon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  List,
  ListItem,
  ListIcon,
  useColorMode,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Divider,
  Progress,
} from "@chakra-ui/react";
import {
  CheckIcon,
  ChevronRightIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import {
  FaWallet,
  FaChartLine,
  FaDollarSign,
  FaShieldAlt,
  FaUsers,
  FaMoneyBillWave,
  FaChartPie,
  FaCalendar,
  FaLock,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getBrandValue } from "../../brandConfig";

const Treasury = () => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 248, 248, 0.9) 50%, rgba(255, 255, 255, 0.95) 100%)`
      : `linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)`;
  const cardBorder =
    colorMode === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)";
  const textPrimary = getColor(
    colorMode === "light" ? "text.primary" : "text.primaryDark",
    colorMode
  );
  const textSecondary = getColor(
    colorMode === "light" ? "text.secondary" : "text.secondaryDark",
    colorMode
  );
  const textMuted = getColor(
    colorMode === "light" ? "text.muted" : "text.mutedDark",
    colorMode
  );

  // Mock data for demonstration
  const treasuryData = {
    totalBalance: 125000,
    usdcBalance: 85000,
    ethBalance: 40000,
    monthlyRevenue: 18500,
    monthlyExpenses: 12000,
    monthlyProfit: 6500,
    distributableProfit: 4550, // 70% of profit
    reserveAddition: 1950, // 30% of profit
    activeTenants: 15,
    avgRevenuePerTenant: 1233,
    tokensSold: 285000,
    tokensRemaining: 215000,
  };

  const revenueBreakdown = [
    { tier: "FOUNDATION ($97/mo)", tenants: 3, revenue: 291 },
    { tier: "GROWTH ($297/mo)", tenants: 2, revenue: 594 },
    { tier: "PROFESSIONAL ($597/mo)", tenants: 4, revenue: 2388 },
    { tier: "COMMUNICATIONS ($897/mo)", tenants: 3, revenue: 2691 },
    { tier: "INTELLIGENCE ($1,497/mo)", tenants: 2, revenue: 2994 },
    { tier: "ENTERPRISE ($2,997/mo)", tenants: 1, revenue: 2997 },
  ];

  const expenseBreakdown = [
    { category: "Infrastructure", amount: 4200, percent: 35 },
    { category: "Development (Tom's Salary)", amount: 5000, percent: 42 },
    { category: "APIs & Services", amount: 1500, percent: 12 },
    { category: "Marketing", amount: 800, percent: 7 },
    { category: "Legal & Accounting", amount: 500, percent: 4 },
  ];

  const monthlyHistory = [
    { month: "July 2025", revenue: 12500, expenses: 9800, profit: 2700 },
    { month: "Aug 2025", revenue: 14200, expenses: 10500, profit: 3700 },
    { month: "Sept 2025", revenue: 16800, expenses: 11200, profit: 5600 },
    { month: "Oct 2025", revenue: 18500, expenses: 12000, profit: 6500 },
  ];

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="1400px" py={16}>
        <VStack spacing={16} align="stretch">
          {/* Hero */}
          <Box textAlign="center">
            <Badge colorScheme="green" fontSize="md" px={4} py={2} mb={4}>
              Treasury Management
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "6xl" }}
              fontWeight="800"
              mb={6}
              color={textPrimary}
              bgGradient="linear(to-r, #10B981, #F59E0B)"
              bgClip="text"
            >
              Complete Transparency
            </Heading>
            <Text fontSize="xl" color={textSecondary} maxW="800px" mx="auto">
              Track revenue, expenses, and profit distributions in real-time. All
              treasury transactions are on-chain and publicly verifiable.
            </Text>
          </Box>

          {/* Treasury Overview */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            <Card
              bg="linear-gradient(135deg, #10B981 0%, #059669 100%)"
              color="white"
              boxShadow="0 8px 24px rgba(16, 185, 129, 0.3)"
            >
              <CardBody p={6}>
                <Stat>
                  <StatLabel fontSize="sm" opacity={0.9}>
                    Total Treasury Balance
                  </StatLabel>
                  <StatNumber fontSize="3xl">
                    ${treasuryData.totalBalance.toLocaleString()}
                  </StatNumber>
                  <StatHelpText fontSize="sm">
                    <StatArrow type="increase" />
                    12.5% from last month
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card
              bg="linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)"
              color="white"
              boxShadow="0 8px 24px rgba(59, 130, 246, 0.3)"
            >
              <CardBody p={6}>
                <Stat>
                  <StatLabel fontSize="sm" opacity={0.9}>
                    Monthly Revenue
                  </StatLabel>
                  <StatNumber fontSize="3xl">
                    ${treasuryData.monthlyRevenue.toLocaleString()}
                  </StatNumber>
                  <StatHelpText fontSize="sm">
                    {treasuryData.activeTenants} active tenants
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card
              bg="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
              color="white"
              boxShadow="0 8px 24px rgba(245, 158, 11, 0.3)"
            >
              <CardBody p={6}>
                <Stat>
                  <StatLabel fontSize="sm" opacity={0.9}>
                    Monthly Profit
                  </StatLabel>
                  <StatNumber fontSize="3xl">
                    ${treasuryData.monthlyProfit.toLocaleString()}
                  </StatNumber>
                  <StatHelpText fontSize="sm">
                    35.1% profit margin
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card
              bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)"
              color="white"
              boxShadow="0 8px 24px rgba(147, 51, 234, 0.3)"
            >
              <CardBody p={6}>
                <Stat>
                  <StatLabel fontSize="sm" opacity={0.9}>
                    Distributable (70%)
                  </StatLabel>
                  <StatNumber fontSize="3xl">
                    ${treasuryData.distributableProfit.toLocaleString()}
                  </StatNumber>
                  <StatHelpText fontSize="sm">
                    Paid to token holders
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Revenue Breakdown */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
              <Flex align="center">
                <Icon as={FaChartPie} w={7} h={7} color="#10B981" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  Revenue by Subscription Tier
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <Table variant="simple" size="lg">
                <Thead>
                  <Tr>
                    <Th color={textSecondary}>Tier</Th>
                    <Th color={textSecondary} isNumeric>
                      Tenants
                    </Th>
                    <Th color={textSecondary} isNumeric>
                      Monthly Revenue
                    </Th>
                    <Th color={textSecondary}>Contribution</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {revenueBreakdown.map((item, index) => {
                    const percentValue = (
                      (item.revenue / treasuryData.monthlyRevenue) *
                      100
                    );
                    const percent = percentValue.toFixed(1);
                    return (
                      <Tr key={index}>
                        <Td fontWeight="600" color={textPrimary}>
                          {item.tier}
                        </Td>
                        <Td isNumeric color={textSecondary}>
                          {item.tenants}
                        </Td>
                        <Td isNumeric fontSize="lg" fontWeight="600" color="#10B981">
                          ${item.revenue.toLocaleString()}
                        </Td>
                        <Td>
                          <Progress value={percentValue} colorScheme="green" borderRadius="full" />
                          <Text fontSize="xs" color={textMuted} mt={1}>
                            {percent}%
                          </Text>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </CardBody>
          </Card>

          {/* Expense Breakdown */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
              <Flex align="center">
                <Icon as={FaDollarSign} w={7} h={7} color="#F59E0B" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  Monthly Expenses
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box>
                  <Table variant="simple">
                    <Tbody>
                      {expenseBreakdown.map((item, index) => (
                        <Tr key={index}>
                          <Td fontWeight="600" color={textPrimary}>
                            {item.category}
                          </Td>
                          <Td isNumeric fontSize="lg" fontWeight="600" color="#F59E0B">
                            ${item.amount.toLocaleString()}
                          </Td>
                          <Td isNumeric color={textSecondary}>
                            {item.percent}%
                          </Td>
                        </Tr>
                      ))}
                      <Tr borderTopWidth="2px" borderTopColor={cardBorder}>
                        <Td fontWeight="700" color={textPrimary}>
                          TOTAL
                        </Td>
                        <Td isNumeric fontSize="xl" fontWeight="700" color={textPrimary}>
                          ${treasuryData.monthlyExpenses.toLocaleString()}
                        </Td>
                        <Td></Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>
                <Box>
                  {expenseBreakdown.map((item, index) => (
                    <Box key={index} mb={4}>
                      <Flex justify="space-between" mb={2}>
                        <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                          {item.category}
                        </Text>
                        <Text fontSize="sm" color={textSecondary}>
                          {item.percent}%
                        </Text>
                      </Flex>
                      <Progress
                        value={item.percent}
                        colorScheme="orange"
                        size="lg"
                        borderRadius="full"
                      />
                    </Box>
                  ))}
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Profit Distribution Calculation */}
          <Card
            bg="linear-gradient(135deg, rgba(147, 51, 234, 0.1), rgba(79, 70, 229, 0.1))"
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px rgba(147, 51, 234, 0.3)"
            borderRadius="2xl"
            borderWidth="2px"
            borderColor="rgba(147, 51, 234, 0.4)"
          >
            <CardHeader
              bg="rgba(147, 51, 234, 0.2)"
              borderBottomWidth="1px"
              borderColor="rgba(147, 51, 234, 0.3)"
            >
              <Flex align="center">
                <Icon as={FaMoneyBillWave} w={7} h={7} color="#9333EA" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  This Month's Distribution Calculation
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <VStack spacing={6} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  <Box
                    p={6}
                    bg="rgba(59, 130, 246, 0.1)"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="rgba(59, 130, 246, 0.3)"
                    textAlign="center"
                  >
                    <Text fontSize="sm" color={textSecondary} mb={2}>
                      Total Revenue
                    </Text>
                    <Text fontSize="3xl" fontWeight="700" color="#3B82F6">
                      ${treasuryData.monthlyRevenue.toLocaleString()}
                    </Text>
                  </Box>
                  <Box
                    p={6}
                    bg="rgba(239, 68, 68, 0.1)"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="rgba(239, 68, 68, 0.3)"
                    textAlign="center"
                  >
                    <Text fontSize="sm" color={textSecondary} mb={2}>
                      Total Expenses
                    </Text>
                    <Text fontSize="3xl" fontWeight="700" color="#EF4444">
                      -${treasuryData.monthlyExpenses.toLocaleString()}
                    </Text>
                  </Box>
                  <Box
                    p={6}
                    bg="rgba(34, 197, 94, 0.1)"
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="rgba(34, 197, 94, 0.3)"
                    textAlign="center"
                  >
                    <Text fontSize="sm" color={textSecondary} mb={2}>
                      Monthly Profit
                    </Text>
                    <Text fontSize="3xl" fontWeight="700" color="#10B981">
                      ${treasuryData.monthlyProfit.toLocaleString()}
                    </Text>
                  </Box>
                </SimpleGrid>

                <Divider />

                <Box
                  p={6}
                  bg="rgba(147, 51, 234, 0.1)"
                  borderRadius="lg"
                  borderWidth="2px"
                  borderColor="rgba(147, 51, 234, 0.4)"
                >
                  <Heading size="md" mb={4} color={textPrimary}>
                    Distribution Split (70/30 Rule)
                  </Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Box textAlign="center">
                      <Text fontSize="sm" color={textSecondary} mb={2}>
                        70% Distributed to Token Holders
                      </Text>
                      <Text fontSize="4xl" fontWeight="800" color="#9333EA">
                        ${treasuryData.distributableProfit.toLocaleString()}
                      </Text>
                      <Text fontSize="xs" color={textMuted} mt={2}>
                        Paid out on the 5th of next month
                      </Text>
                    </Box>
                    <Box textAlign="center">
                      <Text fontSize="sm" color={textSecondary} mb={2}>
                        30% Added to Reserve
                      </Text>
                      <Text fontSize="4xl" fontWeight="800" color="#10B981">
                        ${treasuryData.reserveAddition.toLocaleString()}
                      </Text>
                      <Text fontSize="xs" color={textMuted} mt={2}>
                        For growth and emergency fund
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Box
                  p={4}
                  bg="rgba(245, 158, 11, 0.1)"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="rgba(245, 158, 11, 0.3)"
                >
                  <Flex align="center">
                    <Icon as={InfoIcon} color="#F59E0B" mr={3} />
                    <Text fontSize="sm" color={textSecondary}>
                      <strong>Per Token Payout:</strong> ${(
                        treasuryData.distributableProfit / 1000000
                      ).toFixed(4)}{" "}
                      per BB token this month
                    </Text>
                  </Flex>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* Historical Performance */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
              <Flex align="center">
                <Icon as={FaCalendar} w={7} h={7} color="#3B82F6" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  Historical Performance
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <Table variant="simple" size="lg">
                <Thead>
                  <Tr>
                    <Th color={textSecondary}>Month</Th>
                    <Th color={textSecondary} isNumeric>
                      Revenue
                    </Th>
                    <Th color={textSecondary} isNumeric>
                      Expenses
                    </Th>
                    <Th color={textSecondary} isNumeric>
                      Profit
                    </Th>
                    <Th color={textSecondary} isNumeric>
                      Per Token Payout
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {monthlyHistory.map((item, index) => (
                    <Tr key={index}>
                      <Td fontWeight="600" color={textPrimary}>
                        {item.month}
                      </Td>
                      <Td isNumeric color="#3B82F6" fontWeight="600">
                        ${item.revenue.toLocaleString()}
                      </Td>
                      <Td isNumeric color="#EF4444">
                        ${item.expenses.toLocaleString()}
                      </Td>
                      <Td isNumeric color="#10B981" fontSize="lg" fontWeight="700">
                        ${item.profit.toLocaleString()}
                      </Td>
                      <Td isNumeric color={textSecondary} fontStyle="italic">
                        ${((item.profit * 0.7) / 1000000).toFixed(4)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </CardBody>
          </Card>

          {/* Multisig Security */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
              <Flex align="center">
                <Icon as={FaShieldAlt} w={7} h={7} color="#10B981" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  Treasury Security
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box>
                  <Heading size="md" mb={4} color={textPrimary}>
                    Multi-Signature Wallet
                  </Heading>
                  <Text fontSize="md" mb={4} color={textSecondary}>
                    The treasury is secured by a Gnosis Safe multi-sig wallet
                    requiring 3 of 5 signatures to move funds.
                  </Text>
                  <List spacing={3}>
                    <ListItem color={textSecondary}>
                      <ListIcon as={CheckIcon} color="#10B981" />
                      <strong>3-of-5 signatures required</strong> for all transactions
                    </ListItem>
                    <ListItem color={textSecondary}>
                      <ListIcon as={CheckIcon} color="#10B981" />
                      <strong>On-chain transparency</strong> - All movements visible
                    </ListItem>
                    <ListItem color={textSecondary}>
                      <ListIcon as={CheckIcon} color="#10B981" />
                      <strong>Timelock</strong> on large transactions (&gt;$50K)
                    </ListItem>
                    <ListItem color={textSecondary}>
                      <ListIcon as={CheckIcon} color="#10B981" />
                      <strong>Hardware wallets</strong> for all signers
                    </ListItem>
                  </List>
                </Box>
                <Box
                  p={6}
                  bg="rgba(16, 185, 129, 0.1)"
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor="rgba(16, 185, 129, 0.3)"
                >
                  <Heading size="sm" mb={4} color={textPrimary}>
                    Current Signers
                  </Heading>
                  <VStack spacing={3} align="stretch">
                    {[
                      { name: "Tom Miller", role: "Founder", address: "0x1234...5678" },
                      { name: "Advisory Council #1", role: "Council", address: "0x2345...6789" },
                      { name: "Advisory Council #2", role: "Council", address: "0x3456...7890" },
                      { name: "Advisory Council #3", role: "Council", address: "0x4567...8901" },
                      { name: "Advisory Council #4", role: "Council", address: "0x5678...9012" },
                    ].map((signer, index) => (
                      <Flex
                        key={index}
                        justify="space-between"
                        align="center"
                        p={3}
                        bg="rgba(0, 0, 0, 0.05)"
                        borderRadius="md"
                      >
                        <Box>
                          <Text fontWeight="600" color={textPrimary}>
                            {signer.name}
                          </Text>
                          <Text fontSize="xs" color={textMuted}>
                            {signer.address}
                          </Text>
                        </Box>
                        <Badge colorScheme="green">{signer.role}</Badge>
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* CTA */}
          <Box
            textAlign="center"
            bg="linear-gradient(135deg, #10B981 0%, #059669 100%)"
            p={12}
            borderRadius="2xl"
            color="white"
          >
            <Icon as={FaWallet} w={12} h={12} mb={4} />
            <Heading size="2xl" mb={4}>
              Complete Transparency
            </Heading>
            <Text fontSize="xl" mb={8} maxW="700px" mx="auto">
              All treasury transactions are on-chain and publicly verifiable. Monthly
              reports keep you informed of every dollar.
            </Text>
            <Flex justify="center" gap={4} flexWrap="wrap">
              <Button
                size="lg"
                bg="white"
                color="#10B981"
                rightIcon={<ChevronRightIcon />}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(255, 255, 255, 0.3)",
                }}
                px={10}
                py={7}
                fontSize="xl"
                onClick={() => navigate("/businessbuilder/tokenomics")}
              >
                Invest in Business Builder
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderWidth="2px"
                borderColor="white"
                color="white"
                _hover={{
                  bg: "rgba(255, 255, 255, 0.1)",
                }}
                px={10}
                py={7}
                fontSize="xl"
              >
                View On-Chain Transactions
              </Button>
            </Flex>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Treasury;
